import { z } from 'zod';
import { AssignmentSchema } from './Assignment';

/**
 * Workstream E — a single verified-email entry.
 * Mirrors `VerifiedEmailEntry` in @order-up/models.
 */
const VerifiedEmailEntrySchema = z.object({
  email: z.string().email({ message: 'Verified email must be a valid email address' }),
  verifiedAt: z.date(),
  providerId: z.string().min(1),
}).strict();

// Onboarding lifecycle status — mirrors models/src/auth/types.ts
const OnboardingStatusSchema = z.enum([
  'not_started',
  'in_progress',
  'submitted',
  'approved',
  'declined',
  'completed',
]);

const PhoneNumberSchema = z.object({
  countryCode: z.string().startsWith('+', { message: "Country code must start with a '+'" }),
  number: z.string().min(1, { message: 'Phone number is required' }),
}).strict();

/**
 * User Firestore document schema. `assignments` is the unified scope-aware
 * array of management-app grants (replaces the legacy `tenantAssignments`).
 * Source of truth for role authorization; synced into Firebase custom
 * claims by IClaimsService.
 */
const UserSchema = z
  .object({
    // Core user properties
    uid: z.string().min(1, { message: 'UID is required' }).readonly(),
    email: z.email({ message: 'Valid email is required' }).readonly(),
    displayName: z.string().min(1, { message: 'Display name is required' }).optional().readonly(),
    firstName: z.string().optional().readonly(),
    lastName: z.string().optional().readonly(),
    phoneNumber: PhoneNumberSchema.optional().readonly(),
    // Phase 8: true when `phoneNumber` is proven via a Firebase phone provider.
    // Optional + additive — absent on every legacy doc. Server-trusted only.
    phoneVerified: z.boolean().optional().readonly(),
    photoURL: z.url().nullable().optional().readonly(),
    emailVerified: z.boolean().readonly(),

    // Workstream E — multi-email set. Additive; absent on all legacy docs.
    // Server-trusted only — never accepted from client bodies.
    // Note: no .readonly() here — IUser uses a readonly PROPERTY modifier on
    // a mutable array type; chaining .readonly() on a Zod array would infer a
    // readonly-array type which can't be assigned to VerifiedEmailEntry[].
    verifiedEmails: z.array(VerifiedEmailEntrySchema).optional(),
    // Denormalized address list for Firestore array-contains queries.
    verifiedEmailAddresses: z.array(z.string().email()).optional(),

    // Multi-scope assignments (tenant + restaurant)
    assignments: z.array(AssignmentSchema).default([]).readonly(),

    // User status and metadata
    isActive: z.boolean().readonly(),
    createdAt: z.date().readonly(),
    lastLogin: z.date().optional().readonly(),
    updatedAt: z.date().readonly(),

    // Limbo tracking
    limboStartedAt: z.date().nullable().optional().readonly(),
    limboWarningEmailSentAt: z.date().nullable().optional().readonly(),

    // Onboarding lifecycle
    onboardingStatus: OnboardingStatusSchema.default('not_started').readonly(),
    onboardingApplicationId: z.string().optional().readonly(),
    onboardingStartedAt: z.date().optional().readonly(),
    onboardingSubmittedAt: z.date().optional().readonly(),
    onboardingCompletedAt: z.date().optional().readonly(),
  })
  .strict();

// Schema for creating a new user (onboarding sign-up).
// Server fills in createdAt/updatedAt/assignments.
const CreateUserSchema = z.object({
  uid: z.string().min(1, { message: 'UID is required' }),
  email: z.email({ message: 'Valid email is required' }),
  displayName: z.string().min(2, { message: 'Display name must be at least 2 characters' }),
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  phoneNumber: PhoneNumberSchema.optional(),
  photoURL: z.url().nullable().optional(),
  emailVerified: z.boolean().optional().default(false),
  isActive: z.boolean().optional().default(false),
  onboardingStatus: OnboardingStatusSchema.optional().default('not_started'),
});

// Schema for an ADMIN creating another person's account (POST /admin-users).
// Distinct from CreateUserSchema: the server mints the uid with the Admin SDK,
// so the caller supplies a password and never a uid. The Admin UI once reused
// CreateUserSchema here, which demanded a uid and stripped the password — so
// the modal created the account with the CLIENT sign-up in the admin's own
// session instead, and the new person's profile landed on the admin's uid.
const AdminCreateUserSchema = CreateUserSchema.omit({ uid: true, onboardingStatus: true })
  .extend({
    password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
  })
  .strict();

// Schema for user search (admin tooling)
const UserSearchSchema = z.object({
  searchTerm: z.string().min(1, { message: 'Search term is required' }),
  limit: z.number().positive().max(100).default(20),
});

export type PhoneNumberType = z.infer<typeof PhoneNumberSchema>;
export type UserType = z.infer<typeof UserSchema>;
export type CreateUserType = z.infer<typeof CreateUserSchema>;
export type AdminCreateUserType = z.infer<typeof AdminCreateUserSchema>;
export type UserSearchType = z.infer<typeof UserSearchSchema>;
export type OnboardingStatusType = z.infer<typeof OnboardingStatusSchema>;
export type VerifiedEmailEntryType = z.infer<typeof VerifiedEmailEntrySchema>;

export default UserSchema;
export {
  AdminCreateUserSchema,
  CreateUserSchema,
  OnboardingStatusSchema,
  PhoneNumberSchema,
  UserSchema,
  UserSearchSchema,
  VerifiedEmailEntrySchema,
};
